import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { tenantService } from "@/services/tenantService";
import type {
  Tenant,
  TenantContact,
  TenantConfig,
  TenantUser,
  TenantUserCreate,
  LoadTestRunRequest,
} from "@/types";

export function useTenants(params?: { search?: string; status?: string }) {
  return useQuery({
    queryKey: ["tenants", params],
    queryFn: () => tenantService.list(params),
  });
}

export function useTenant(id: string) {
  return useQuery({
    queryKey: ["tenant", id],
    queryFn: () => tenantService.get(id),
    enabled: !!id,
  });
}

export function useTenantStats() {
  return useQuery({
    queryKey: ["tenant-stats"],
    queryFn: tenantService.stats,
  });
}

export function useCreateTenant() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: Partial<Tenant>) => tenantService.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["tenants"] });
      queryClient.invalidateQueries({ queryKey: ["tenant-stats"] });
    },
  });
}

export function useUpdateTenant() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<Tenant> }) =>
      tenantService.update(id, data),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ["tenants"] });
      queryClient.invalidateQueries({ queryKey: ["tenant", variables.id] });
      queryClient.invalidateQueries({ queryKey: ["tenant-stats"] });
    },
  });
}

export function useDeleteTenant() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => tenantService.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["tenants"] });
      queryClient.invalidateQueries({ queryKey: ["tenant-stats"] });
    },
  });
}

export function useTenantConfig(tenantId: string) {
  return useQuery({
    queryKey: ["tenant-config", tenantId],
    queryFn: () => tenantService.getConfig(tenantId),
    enabled: !!tenantId,
  });
}

export function useUpdateTenantConfig() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      tenantId,
      data,
    }: {
      tenantId: string;
      data: Partial<TenantConfig>;
    }) => tenantService.updateConfig(tenantId, data),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: ["tenant-config", variables.tenantId],
      });
      queryClient.invalidateQueries({
        queryKey: ["tenant", variables.tenantId],
      });
    },
  });
}

export function useTenantContacts(tenantId: string) {
  return useQuery({
    queryKey: ["tenant-contacts", tenantId],
    queryFn: () => tenantService.listContacts(tenantId),
    enabled: !!tenantId,
  });
}

export function useCreateTenantContact() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      tenantId,
      data,
    }: {
      tenantId: string;
      data: Partial<TenantContact>;
    }) => tenantService.createContact(tenantId, data),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: ["tenant-contacts", variables.tenantId],
      });
      queryClient.invalidateQueries({
        queryKey: ["tenant", variables.tenantId],
      });
    },
  });
}

export function useDeleteTenantContact() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      tenantId,
      contactId,
    }: {
      tenantId: string;
      contactId: string;
    }) => tenantService.deleteContact(tenantId, contactId),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: ["tenant-contacts", variables.tenantId],
      });
      queryClient.invalidateQueries({
        queryKey: ["tenant", variables.tenantId],
      });
    },
  });
}

// Service Catalog
export function useServiceCatalog() {
  return useQuery({
    queryKey: ["service-catalog"],
    queryFn: tenantService.getServiceCatalog,
  });
}

export function useTenantServices(tenantId: string) {
  return useQuery({
    queryKey: ["tenant-services", tenantId],
    queryFn: () => tenantService.getTenantServices(tenantId),
    enabled: !!tenantId,
  });
}

export function useUpdateTenantServices() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      tenantId,
      services,
    }: {
      tenantId: string;
      services: Array<{ service_id: string; enabled: boolean }>;
    }) => tenantService.updateTenantServices(tenantId, services),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: ["tenant-services", variables.tenantId],
      });
      queryClient.invalidateQueries({
        queryKey: ["tenant", variables.tenantId],
      });
    },
  });
}

// Credentials (per-tenant API keys / secrets)
export function useTenantCredentials(tenantId: string) {
  return useQuery({
    queryKey: ["tenant-credentials", tenantId],
    queryFn: () => tenantService.getCredentials(tenantId),
    enabled: !!tenantId,
  });
}

export function useUpdateTenantCredentials() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      tenantId,
      credentials,
    }: {
      tenantId: string;
      credentials: Array<{ key: string; value: string }>;
    }) => tenantService.updateCredentials(tenantId, credentials),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: ["tenant-credentials", variables.tenantId],
      });
    },
  });
}

// Deployment
export function useDeployReadiness(tenantId: string) {
  return useQuery({
    queryKey: ["deploy-readiness", tenantId],
    queryFn: () => tenantService.getDeployReadiness(tenantId),
    enabled: !!tenantId,
  });
}

export function useDeployTenant() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (tenantId: string) => tenantService.deployTenant(tenantId),
    onSuccess: (_data, tenantId) => {
      queryClient.invalidateQueries({ queryKey: ["tenant", tenantId] });
      queryClient.invalidateQueries({ queryKey: ["tenants"] });
      queryClient.invalidateQueries({ queryKey: ["tenant-stats"] });
    },
  });
}

// Poll tenant data while deployment is running
export function useDeploymentPoller(tenantId: string, isDeploying: boolean) {
  return useQuery({
    queryKey: ["tenant", tenantId],
    queryFn: () => tenantService.get(tenantId),
    enabled: !!tenantId && isDeploying,
    refetchInterval: isDeploying ? 2000 : false,
  });
}

// Tenant Users (Central Auth)
export function useTenantUsers(tenantId: string) {
  return useQuery({
    queryKey: ["tenant-users", tenantId],
    queryFn: () => tenantService.listUsers(tenantId),
    enabled: !!tenantId,
  });
}

export function useCreateTenantUser() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      tenantId,
      data,
    }: {
      tenantId: string;
      data: TenantUserCreate;
    }) => tenantService.createUser(tenantId, data),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: ["tenant-users", variables.tenantId],
      });
    },
  });
}

export function useUpdateTenantUser() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      tenantId,
      userId,
      data,
    }: {
      tenantId: string;
      userId: string;
      data: Partial<TenantUser> & { password?: string };
    }) => tenantService.updateUser(tenantId, userId, data),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: ["tenant-users", variables.tenantId],
      });
    },
  });
}

export function useDeleteTenantUser() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ tenantId, userId }: { tenantId: string; userId: string }) =>
      tenantService.deleteUser(tenantId, userId),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: ["tenant-users", variables.tenantId],
      });
    },
  });
}

// Booking load tester
export function useLoadTest(tenantId: string) {
  return useQuery({
    queryKey: ["load-test", tenantId],
    queryFn: () => tenantService.getLoadTest(tenantId),
    enabled: !!tenantId,
  });
}

export function useRunLoadTest() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      tenantId,
      params,
    }: {
      tenantId: string;
      params: LoadTestRunRequest;
    }) => tenantService.runLoadTest(tenantId, params),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: ["load-test", variables.tenantId],
      });
    },
  });
}

export function usePurgeLoadTest() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (tenantId: string) => tenantService.purgeLoadTest(tenantId),
    onSuccess: (_data, tenantId) => {
      queryClient.invalidateQueries({ queryKey: ["load-test", tenantId] });
    },
  });
}
